import {
  LoginInput,
  LoginOutput,
  LogOutInput,
  LogOutOutput,
  SubscribeOutput,
  UserSession,
} from '@allocab/allocab-commons-shared-types';
import { SubscribeInput } from '@allocab/allocab-driver-shared-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import jwtDecode from 'jwt-decode';

import { useTokens } from './useTokens';
import { driverApi } from '../services/network/Api';
import { ToastManager } from '../services/toast/utils';
import { t } from '../services/translation/translation';
import { noop } from '../utils/common';

const signin = (input: LoginInput) =>
  driverApi.post<LoginInput, LoginOutput>('/authentication/v1/user/login', input, {
    skipAuth: true,
  });

const signup = (input: SubscribeInput) =>
  driverApi.post<SubscribeInput, SubscribeOutput>('/authentication/v1/user/subscribe/fleet-manager', input, {
    skipAuth: true,
  });

const logout = async (accessToken?: string) => {
  if (!accessToken) return null;

  return driverApi.post<LogOutInput, LogOutOutput>(
    '/authentication/v1/user/logout',
    { accessToken, pushToken: 'TODO' },
    { skipAuth: true },
  );
};

export const useAuth = () => {
  const { tokens, updateTokens, clearTokens } = useTokens();
  const queryClient = useQueryClient();

  const handleSuccessfulSignin = (tokens: LoginOutput) => {
    const session = jwtDecode(tokens.accessToken) as UserSession;

    if (session.kind !== 'FLEET_MANAGER') {
      ToastManager.show({ message: t('login-route.warn-passenger'), severity: 'warning' });
      return;
    }

    updateTokens(queryClient, tokens);
  };

  const signinMutation = useMutation<LoginOutput, unknown, LoginInput>({
    mutationFn: signin,
    onSuccess: tokens => handleSuccessfulSignin(tokens),
  });

  const signupMutation = useMutation<SubscribeOutput, unknown, SubscribeInput>({
    mutationFn: signup,
    onSuccess: async tokens => updateTokens(queryClient, tokens),
  });

  const logoutMutation = useMutation({
    mutationFn: () => logout(tokens?.accessToken),
    onMutate: () => {
      clearTokens(queryClient);
    },
    onError: () => noop(),
  });

  return {
    signinMutation,
    signupMutation,
    logoutMutation,
  };
};
